import React, { useMemo, useState } from 'react';
import { Select, Option } from '@jobber/components/Select';
import Pagination from '../../../components/Common/Pagination';
import { getFromServer, postToServer, deleteFromServer } from '../../../components/Common/requests';
import { showToast } from '@jobber/components/Toast';
import './table.css';
import ShowAppliedFilters from '../../../components/Common/ShowAppliedFilters';
import JobFilters from './Filters/JobFilters';
import TopButtons from '../../Jobs/Buttons/TopButtons';
import { useJobContext } from '../contextAPI/JobContext';
import { useMutation, useQuery as useTanStackQuery } from '@tanstack/react-query';
import { InputText } from '@jobber/components/InputText';
import { Button } from '@jobber/components/Button';
import { Glimmer } from '@jobber/components/Glimmer';
import { Checkbox } from '@jobber/components/Checkbox';
import { Spinner } from '@jobber/components/Spinner';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import {
    DndContext,
    MouseSensor,
    TouchSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    closestCenter
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    horizontalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IoMdArrowDropdown, IoMdArrowDropup } from 'react-icons/io';
import { Col, Dropdown, DropdownItem, Row } from 'react-bootstrap';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import jobColumns from './useTableColumns';
import { useDebounce } from '../../../utils/useDebounce';
import { useAutomationFilters } from '../FilterQuery/AutomationFilterQuery';
import FailedTaskModal from '../../../components/FailedTaskModal';

const SortableHeader = ({ header, onSort, getIconStyle }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: header.id });
    const headerStyle = {
        position: 'relative',
        transition,
        transform: CSS.Translate.toString(transform),
        width: `calc(var(--header-${header?.id}-size) * 1px)`,
        zIndex: isDragging ? 1 : 0,
    };
    const { isDisabled, header: headerVal, filterKey } = header.column.columnDef;
    return (
        <th
            ref={!!isDisabled ? undefined : setNodeRef}
            colSpan={header.colSpan}
            style={headerStyle}
            className="border p-2"
        >
            <div {...attributes} {...listeners} className={`headDND ${!!isDisabled && 'disabledDND'}`} />
            <div className='d-flex justify-content-between align-items-center'>
                <b style={{ zIndex: 2 }} className='position-relative'>
                    {header.isPlaceholder ? null : flexRender(headerVal, header.getContext())}
                </b>
                {!!filterKey &&
                    <div className='icon-container' onClick={(e) => { e.stopPropagation(); onSort(filterKey); }}>
                        <IoMdArrowDropup className={`icon-up ${getIconStyle(filterKey, 'ASCENDING')}`} />
                        <IoMdArrowDropdown className={`icon-down ${getIconStyle(filterKey, 'DESCENDING')}`} />
                    </div>
                }
            </div>
            <div
                onMouseDown={header.getResizeHandler()}
                onTouchStart={header.getResizeHandler()}
                className={`${!!isDisabled ? 'd-none' : 'resizer'} ${header.column.getIsResizing() ? 'isResizing' : ''}`}
            />
        </th>
    );
};

const DragAlongCell = ({ cell }) => {
    const { isDragging, setNodeRef, transform, transition } = useSortable({
        id: cell.column.id,
    });
    const style = {
        position: 'relative',
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: isDragging ? 1 : 0,
    };

    return (
        <td style={style} ref={setNodeRef} className="p-2">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
    );
};

export default function JobTable() {
    const { updateFilter, buildQuery, filters } = useAutomationFilters();
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ASCENDING' });
    const [search, setSearch] = useState('');
    const debouncedUpdateFilter = useDebounce(updateFilter, 500);

    const localSettings = JSON.parse(localStorage.getItem('jobSettings')) || {};
    console.log(localSettings);
    const [columnVisibility, setColumnVisibility] = useState(localSettings?.columnVisibility || {});
    const [columnOrder, setColumnOrder] = useState(() => Array.isArray(localSettings?.columnOrder)
        ? localSettings?.columnOrder
        : Object.entries(localSettings?.columnOrder || {})
            .sort((a, b) => a[1] - b[1])
            .map(([key]) => key.replace(/^col/, '')));
    const [columnSizing, setColumnSizing] = useState(localSettings?.columnWidths || {});
    const [showDropdown, setShowDropdown] = useState(false);
    const toggleDropdown = () => setShowDropdown(!showDropdown);
    const [modalOpen, setModalOpen] = useState(false);
    const [failedData, setFailedData] = useState();

    const onSort = (key) => {
        updateFilter('sort_key', key);
        if (sortConfig?.key === key && sortConfig?.direction === 'ASCENDING') {
            setSortConfig({ key, direction: 'DESCENDING' });
            updateFilter('sort_direction', 'DESCENDING');
        } else {
            setSortConfig({ key, direction: 'ASCENDING' });
            updateFilter('sort_direction', 'ASCENDING');
        }
    };

    const queryString = buildQuery();
    const { data: tableData, isLoading, refetch } = useTanStackQuery({
        queryKey: ['jobs', filters, currentPage, queryString, limit, sortConfig],
        queryFn: ({ signal }) => getFromServer(`jobs?${queryString}`, signal),
        staleTime: 1000 * 60 * 5,
        retry: 2,
    });

    const data = tableData?.jobs || [];
    const totalPage = tableData?.totalPages || 1;
    const entries = tableData?.totalJobs || 0;

    const reloadFromFirstPage = () => {
        setCurrentPage(1);
        updateFilter('page', 1);
        if (currentPage === 1) {
            refetch();
        }
    };

    // Save settings mutation.
    const { mutate: mutateSettings, isLoading: isSavePending } = useMutation({
        mutationFn: (body) => postToServer('job-table-settings', body),
        onError: (error) => {
            if (error && error.status === 404) {
                showToast({ message: 'Error 404: Table settings endpoint not found.', variation: 'error' });
            } else {
                showToast({ message: error.message || error.error || 'An error occurred', variation: 'error' });
            }
        },
        onSuccess: (data) => {
            localStorage.setItem('jobSettings', JSON.stringify(data.data));
            showToast({ message: 'Job settings updated successfully' });
            refetch();
        },
    });

    function saveUserSettingsHandler() {
        mutateSettings({
            columnOrder,
            columnVisibility,
            columnWidths: columnSizing,
        })
    };

    const { mutate: mutateReOpen, isLoading: isReOpenPending } = useMutation({
        mutationFn: (row) => postToServer(`reopen-job/${row?.jobId}/${row?._id}`),
        onError: (error) => {
            showToast({
                message: error.message || error.error || "An error occurred",
                variation: "error",
            });
        },
        onSuccess: (result, row) => {
            if (result.status) {
                reloadFromFirstPage();
                showToast({ message: "Job reopended successfully" });
            } else {
                row.setFailedData && row.setFailedData(row);
                console.log(result);
                openModal && openModal(true);
                let errorMessage = "An error occurred.";
                if (result.message && typeof result.message === "string") {
                    errorMessage = result.message;
                } else if (result.error && typeof result.error === "string") {
                    errorMessage = result.error;
                }
                showToast({ message: errorMessage, variation: "error" });
            }
        },
    });

    const reOpenHandler = (row, setFailedDataCallback) => {
        mutateReOpen({ ...row, setFailedData: setFailedDataCallback });
    };

    const { mutate: deleteJob, isLoading: isDeletePending } = useMutation({
        mutationFn: (url) => deleteFromServer(`job/${url}`),
        onError: (error) => {
            showToast({ message: error.message || error.error || 'An error occurred', variation: 'error' });
        },
        onSuccess: (_, variables) => {
            showToast({ message: "Job have been successfully deleted" });
            reloadFromFirstPage();
        },
    });

    const getIconStyle = (key, direction) => {
        return sortConfig.key === key && sortConfig.direction === direction ? 'fs-5 text-dark' : 'fs-5 text-muted opacity-25';
    };

    // Selected rows state from useJobContext.
    const { selectedIds, setSelectedIds } = useJobContext();
    const [isAllSelected, setIsAllSelected] = useState(false);
    const handleRowCheck = (id, isChecked) => {
        if (isChecked) {
            if (!selectedIds.includes(id)) {
                setSelectedIds([...selectedIds, id]);
            }
        } else {
            setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
        }
    };
    const handleMasterCheck = (isChecked) => {
        setIsAllSelected(isChecked);
        if (isChecked) {
            const allIds = data.map((item) => item.jobId);
            setSelectedIds([...allIds]);
        } else {
            setSelectedIds([]);
        }
    };

    const columns = jobColumns({ isAllSelected, selectedIds, handleMasterCheck, handleRowCheck });

    const table = useReactTable({
        data: data,
        columns,
        defaultColumn: { minSize: 100, maxSize: 600 },
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        state: { columnVisibility, columnOrder, columnSizing },
        onColumnSizingChange: setColumnSizing,
        columnResizeMode: 'onChange',
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        meta: {
            reLoading: isReOpenPending,
            reOpenHandler: reOpenHandler,
            deleteHandler: (url, msg) => deleteJob(url, { msg }),
        }
    });

    function formatLabel(key) {
        return key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
    }

    // Compute header sizes for styling.
    const columnSizeVars = useMemo(() => {
        const headers = table.getFlatHeaders();
        const colSizes = {};
        for (let i = 0; i < headers.length; i++) {
            const header = headers[i];
            colSizes[`--header-${header.id}-size`] = header.getSize();
            colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
        }
        localStorage.setItem('jobSettings', JSON.stringify({ ...localSettings, columnWidths: columnSizing }));
        return colSizes;
    }, [table.getState().columnSizingInfo, table.getState().columnSizing]);

    // DnD sensors drag-and-drop.
    const sensors = useSensors(
        useSensor(MouseSensor, {}),
        useSensor(TouchSensor, {}),
        useSensor(KeyboardSensor, {})
    );
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!active || !over || active.id === 'checkbox' || over.id === 'checkbox') return;
        if (active && over && active.id !== over.id) {
            setColumnOrder((oldOrder) => {
                const activeIndex = oldOrder.indexOf(active.id);
                const overIndex = oldOrder.indexOf(over.id);
                const newOrder = arrayMove(oldOrder, activeIndex, overIndex);
                localStorage.setItem('jobSettings', JSON.stringify({ ...localSettings, columnOrder: newOrder }));
                return [...newOrder];
            });
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        updateFilter('page', page);
    };

    return (
        <>
            <div className="mb-5">
                <ShowAppliedFilters page={'automation'} />
            </div>
            <div className="row mb-3">
                <div className="col-lg-3 col-md-4 d-flex gap-2 align-items-center">
                    <p className="pt-3">Show</p>
                    <Select
                        size="small"
                        onChange={(e) => {
                            updateFilter('limit', e);
                            setLimit(e);
                        }}
                        value={limit}
                    >
                        <Option value="all">All (Takes time to load)</Option>
                        <Option value="10">10</Option>
                        <Option value="20">20</Option>
                        <Option value="50">50</Option>
                        <Option value="100">100</Option>
                    </Select>
                    <p className="pt-3">entries</p>
                </div>
                <div className="col-md-9 mt-2">
                    <div className="d-flex gap-2 flex-wrap justify-content-end align-items-center" style={{ paddingBottom: '1rem' }}>
                        <JobFilters />
                    </div>
                </div>
            </div>
            <FailedTaskModal title='Failed to reopen following jobs!' failedData={[failedData]} modalOpen={modalOpen} setModalOpen={setModalOpen} />
            <Row>
                <Col md={6}>
                    <div className="d-flex gap-3 pt-3 align-items-center">
                        <InputText
                            placeholder="Search"
                            size="small"
                            value={search}
                            onChange={(e) => {
                                debouncedUpdateFilter('searchTerm', e);
                                setCurrentPage(1);
                                setSearch(e);
                            }}
                        />
                        <div style={{ position: 'relative' }}>
                            <Button label="Manage Columns" onClick={toggleDropdown} />
                            {showDropdown && (
                                <div className="dropdown__sticky">
                                    <Dropdown show={showDropdown}>
                                        <Dropdown.Menu>
                                            <DropdownItem onClick={toggleDropdown}>Show and Hide Columns</DropdownItem>
                                            <div className="ms-3 d-flex flex-column gap-[3px]">
                                                {table.getAllLeafColumns()
                                                    .filter(column => column.id !== 'checkbox' && column.id !== 'reopen')
                                                    .map((column) => (
                                                        <div key={column.id} className="px-1">
                                                            <label>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={column.getIsVisible()}
                                                                    onChange={column.getToggleVisibilityHandler()}
                                                                />{' '}
                                                                {formatLabel(column.id)}
                                                            </label>
                                                        </div>
                                                    ))}
                                            </div>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            )}
                        </div>

                        {isSavePending ? (<Spinner size="base" />) : (<Button label="Save Settings" type="secondary" onClick={saveUserSettingsHandler} />)}
                    </div>
                </Col>

                <Col md={6} className="text-end pt-3">
                    <TopButtons
                        sync={() => {
                            reloadFromFirstPage();
                        }}
                        currentPage="jobs"
                    />
                </Col>
            </Row>

            <div className="mt-3 rounded-lg bg-white overflow-auto position-relative">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToHorizontalAxis]}
                    onDragEnd={handleDragEnd}
                >
                    <table
                        className="table table-hover border-collapse border border-gray-300"
                        style={{ ...columnSizeVars, width: table.getTotalSize() }}
                    >
                        <thead className="bg-gray-100">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <SortableContext
                                    key={headerGroup.id}
                                    items={columnOrder}
                                    strategy={horizontalListSortingStrategy}
                                >
                                    <tr key={headerGroup.id} className='pt-4' style={{ height: '60px' }}>
                                        {headerGroup.headers.map((header) => (
                                            <SortableHeader
                                                key={header.id}
                                                header={header}
                                                onSort={onSort}
                                                getIconStyle={getIconStyle}
                                            />
                                        ))}
                                    </tr>
                                </SortableContext>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading
                                ? [...Array(2)]?.map((_, index) => (
                                    <tr key={index}>
                                        {table.getVisibleFlatColumns().map((column) => (
                                            <td key={column.id}>
                                                <Glimmer shape="rectangle" size="base" timing="speed" />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                                : data && data.length > 0
                                    ? table.getRowModel().rows.map((row) => (
                                        <tr key={row.id} className="border-bottom">
                                            {row.getVisibleCells().map((cell) => (
                                                <SortableContext
                                                    key={cell.id}
                                                    items={columnOrder}
                                                    strategy={horizontalListSortingStrategy}
                                                >
                                                    <DragAlongCell key={cell.id} cell={cell} />
                                                </SortableContext>
                                            ))}
                                        </tr>
                                    ))
                                    : (table.getRowModel().rows.length === 0 && (
                                        <tr>
                                            <td colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem' }}>
                                                No data found
                                            </td>
                                        </tr>
                                    ))}
                        </tbody>
                    </table>
                </DndContext>
            </div>
            <div className="d-md-flex justify-content-between">
                <div>
                    <p>{entries} entries</p>
                </div>
                {totalPage > 1 && (
                    <div>
                        <Pagination currentPage={tableData.currentPage} totalPages={totalPage} onPageChange={handlePageChange} />
                    </div>
                )}
            </div>
        </>
    );
}
